export const en = {
  game: {
    title: "Let's Play!",
    description: "Jump through my skills and projects in this fun mini-game. Can you unlock them all?",
    pressToStart: "Press SPACE or tap to start",
    instructions: "Jump over the bugs to unlock skills and projects!",
    gameOver: "Game Over!",
    finalScore: "Final Score",
    unlockedSkills: "Skills Unlocked",
    unlockedProjects: "Projects Unlocked",
    playAgain: "Play Again",
    contactMe: "Contact Me"
  },
  footer: {
    tagline: "Full Stack Developer passionate about creating elegant solutions to complex problems.",
    navigation: {
      title: "Navigation",
      home: "Home",
      about: "About",
      skills: "Skills",
      projects: "Projects"
    },
    contact: {
      title: "Contact",
      email: "Email",
      github: "GitHub",
      linkedin: "LinkedIn"
    },
    legal: {
      title: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service"
    },
    copyright: "© 2024 Moustapha Sambe. All rights reserved."
  },
  blog: {
    title: "Blog",
    subtitle: "Sharing my thoughts and experiences in web development",
    readMore: "Read More",
    loading: "Loading posts...",
    error: "Error loading posts",
    noPosts: "No posts available at the moment.",
    posts: {
      webApp: {
        title: "Building Modern Web Applications with React",
        summary: "A comprehensive guide to building performant and scalable web applications using React and modern frontend tools.",
        category: "Web Development"
      },
      cicd: {
        title: "CI/CD Pipelines for Modern Applications",
        summary: "Exploring best practices for implementing continuous integration and deployment workflows for your projects.",
        category: "DevOps"
      },
      mobile: {
        title: "From Web to Mobile: Cross-Platform Development",
        summary: "How to leverage your web development skills to build native-like mobile applications for iOS and Android.",
        category: "Mobile Development"
      }
    },
    labels: {
      comingSoon: "Coming Soon",
      futurePost: "Future Post"
    }
  },
  nav: {
    home: "Home",
    about: "About",
    skills: "Skills",
    projects: "Projects",
    contact: "Contact",
  },
  hero: {
    greeting: "Hi, I'm",
    role: "Data Engineer & Full Stack Developer",
    roles: [
      "Data Engineer",
      "AI Enthusiast",
      "Full Stack Developer",
      "DevOps Enthusiast",
      "ETL Pipeline Developer",
      "API Integration Engineer",
      "Web Developer"
    ],
    description: "I build exceptional digital experiences that make life easier and more enjoyable.",
    cta: "Get in touch",
    scrollDown: "Scroll Down",
    viewProjects: "View Projects"
  },
  experience: {
    title: "Work Experience",
    subtitle: "My professional journey through various roles and responsibilities.",
    present: "Present",
    items: {
      cap4lab: {
        period: "October 2023 - September 2025",
        title: "Data Integration Engineer",
        description: "Development and orchestration of data flows and APIs with MuleSoft.",
        skills: [
          "Data flow development and orchestration",
          "Secure API design with MuleSoft",
          "Integration pipeline implementation",
          "Testing with MUnit",
          "CI/CD with Jenkins and GitLab",
          "Deployment on CloudHub",
          "Cross-application data transformation",
          "TLS & API security"
        ]
      }
    },
    roles: {
      apprenticeIT: "Junior Integration Engineer",
      apprenticeTech: "Web Developer",
      webDev: "Web Developer Intern",
      mobileDev: "Mobile Developer",
      dataAnalyst: "Data Analyst"
    },
    descriptions: {
      apprenticeIT: "API development and integration focusing on Mule 4. Unit testing with MUnit, API documentation, Mule 4 custom policy development, RAML design, and Git versioning.",
      apprenticeTech: "Development of a FAQ application for learners and instructors. Built the architecture and interfaces, created the database.",
      webDev: "Development of a mobile FAQ application with React Native and Firebase. Created UI/UX design and implemented interactive features.",
      mobileDev: "Development of a checkers-like board game on Google Play Store. Initially developed for Android with Java and Eclipse, currently being redeveloped with React and Node.js for iOS and Android.",
      dataAnalyst: "Sales analysis project using SQL and Power BI. Created interactive dashboards to visualize key performance indicators. Extracted, transformed and aggregated data using SQL. Developed visualizations with Power BI and DAX."
    },
    skills: {
      projectManagement: "Project Management",
      networkSecurity: "Network Security",
      fullStack: "Full Stack Development",
      sql: "SQL",
      dbAdmin: "Database Administration",
      automation: "Automation",
      html: "HTML5",
      python: "Python",
      javascript: "JavaScript",
      paperjs: "Paper.js",
      java: "Java",
      reactjs: "React.js",
      nodejs: "Node.js",
      androidStudio: "Android Studio",
      docker: "Docker",
      git: "Git",
      agile: "Agile Methodology",
      teamwork: "Teamwork",
      communication: "Communication",
      problemSolving: "Problem Solving",
      adaptability: "Adaptability",
      creativity: "Creativity"
    }
  },
  about: {
    title: "About Me",
    description: "A passionate Data Engineer with a strong foundation in both data technologies and full stack development. I love turning complex problems into simple, beautiful, and intuitive solutions.",
    yearsExperience: "Years",
    passionate: "Master's student in Data Engineering & Artificial Intelligence at ESGI Paris, passionate about data, artificial intelligence and distributed systems.",
    currentStudy: "I am currently looking for an apprenticeship in Data Analyst, Data Scientist or Artificial Intelligence starting September 2026, for a duration of 12 to 24 months with a schedule of 3 weeks in company and 1 week at school.",
    hobbies: "I have experience in data integration, ETL pipeline development and secure API design with MuleSoft. Curious, autonomous and rigorous, I enjoy designing intelligent data solutions combining Machine Learning, data visualization and automation.",
    languages: "Languages",
    languageNames: "French (Fluent), English (Advanced)",
    downloadCV: "Download CV",
  },
  skills: {
    title: "My Skills",
    subtitle: "A comprehensive toolkit of technologies and methodologies I've mastered throughout my journey.",
    sections: {
      programmingLanguages: "Programming Languages",
      dataAndAI: "Data & AI",
      webTechnologies: "Web Technologies",
      devOpsAndCloud: "DevOps & Cloud",
      databases: "Databases",
      softSkills: "Soft Skills"
    },
    softSkillItems: {
      teamwork: "Teamwork",
      autonomy: "Autonomy",
      curiosity: "Curiosity",
      rigor: "Rigor",
      creativity: "Creativity",
      adaptability: "Adaptability",
      communication: "Communication"
    }
  },
  projects: {
    title: "My Projects",
    subtitle: "Explore a selection of my latest projects, showcasing my skills and experience.",
    viewProject: "View Project",
    liveDemo: "Live Demo",
    viewSourceCode: "View source code",
    viewAllProjects: "View All Projects on GitHub",
    learnMore: "Learn more",
    fullscreen: "View fullscreen",
    items: {
      joj2026: {
        title: "Dakar Urban Traffic – JOJ 2026",
        description:
          "Data analysis and urban traffic prediction project using Python and Machine Learning to anticipate congestion during the 2026 Youth Olympic Games.",
        clientType: "Data & Machine Learning",
        imageAlt: "JOJ 2026 project preview",
      },
      burnoutCheck: {
        title: "BurnoutCheck",
        description:
          "Intelligent application for predicting burnout risk based on Machine Learning and behavioral data, with SHAP explainability.",
        clientType: "AI & Data",
        imageAlt: "BurnoutCheck preview",
      },
      socabeg: {
        title: "SOCABEG",
        description:
          "Professional website for a construction company featuring services, completed projects, and an online quote request system.",
        clientType: "Construction",
        imageAlt: "SOCABEG website preview",
      },
      khalilCollection: {
        title: "Khalil Collection",
        description:
          "Online store for a premium fashion brand with a filterable catalog, custom orders, and integrated stock management.",
        clientType: "Fashion & e-commerce",
        imageAlt: "Khalil Collection preview",
      },
      noppalJaay: {
        title: "Noppal Jaay",
        description:
          "Conversational commerce SaaS enabling merchants to create an online store, manage their catalog and orders with WhatsApp integration.",
        clientType: "SaaS & retail",
        imageAlt: "Noppal Jaay preview",
      },
      magouGlow: {
        title: "MagouGlow Shop",
        description:
          "Premium e-commerce store specializing in skincare and beauty products with a modern user experience.",
        clientType: "Beauty & e-commerce",
        imageAlt: "MagouGlow Shop preview",
      },
      sengeoMap: {
        title: "SengeoMap",
        description:
          "Interactive web mapping application for visualizing and exploring geographic data.",
        clientType: "Mapping & data",
        imageAlt: "SengeoMap preview",
      },
      yoonExpress: {
        title: "Yoon Express",
        description:
          "Intercity shuttle booking platform with search, reservation, secure payment, and user, driver, and admin dashboards.",
        clientType: "Mobility & SaaS",
        imageAlt: "Yoon Express preview",
      },
      rentyx: {
        title: "Rentyx",
        description:
          "Digital online booking and rental platform with a smooth and modern user experience.",
        clientType: "Rental & booking",
        imageAlt: "Rentyx preview",
      },
    }
  },
  education: {
    title: "Education",
    subtitle: "My academic journey and educational qualifications.",
    items: {
      esgiMaster: {
        degree: "Master's in Data Engineering & Artificial Intelligence",
        description: "Specialization in Data Engineering, AI, Machine Learning and distributed systems."
      },
      estiamMaster: {
        degree: "Master's in Full Lifecycle API Management",
        description: "Advanced training in API development, security and management."
      },
      epsiBachelor: {
        degree: "Bachelor DevOps CDA Full Stack",
        description: "Training in full stack development, DevOps and software architecture."
      }
    }
  },
  // GitHub section removed
  contact: {
    title: "Get In Touch",
    subtitle: "Have a project in mind or just want to say hello? Feel free to reach out!",
    info: {
      title: "Contact Information",
      description: "Feel free to contact me through any of these channels. I'm always open to discussing new projects, opportunities, or partnerships.",
      location: {
        title: "Location",
        value: "Paris, 94800"
      },
      email: {
        title: "Email",
        value: "moustaphasambe719@gmail.com"
      },
      phone: {
        title: "Phone",
        value: "+33 7 53 84 81 57"
      },
      social: {
        title: "Follow Me"
      }
    },
    form: {
      title: "Send a Message",
      name: {
        label: "Name",
        placeholder: "Your name"
      },
      email: {
        label: "Email",
        placeholder: "Your email address"
      },
      subject: {
        label: "Subject",
        placeholder: "What's this about?",
        options: {
          project: "Project Proposal",
          job: "Job Opportunity",
          collaboration: "Collaboration Proposal",
          question: "Technical Question",
          feedback: "Portfolio Feedback",
          other: "Other Topic"
        }
      },
      message: {
        label: "Message",
        placeholder: "Your message"
      },
      submit: {
        label: "Send Message",
        sending: "Sending..."
      }
    },
    toast: {
      success: {
        title: "Message sent!",
        description: "Thanks for contacting me. I'll get back to you soon!"
      },
      error: {
        title: "Error",
        description: "There was a problem sending your message. Please try again."
      }
    }
  },
};
